/**
 * ASCII Art Generator - 鼠标位置追踪器
 *
 * 近踪 canvas 上的鼠标位置，支持归一化坐标和触摸事件
 */

import type { MouseState, MouseTrackerOptions } from './types'

/**
 * 鼠标追踪器
 *
 * 提供 canvas 上的鼠标位置追踪，支持：
 * - 实时位置更新
 * - 归一化坐标计算
 * - 进入/离开检测
 * - 触摸事件支持
 */
export class MouseTracker {
  private canvas: HTMLCanvasElement | null = null
  private state: MouseState = {
    x: 0,
    y: 0,
    isInCanvas: false,
    normalizedX: 0,
    normalizedY: 0
  }

  private lastTouchTime = 0
  private touchThrottle: number
  private enableTouch: boolean

  constructor(options: MouseTrackerOptions = {}) {
    this.enableTouch = options.enableTouch ?? true
    this.touchThrottle = options.touchThrottle ?? 16 // 约 60fps
  }

  /**
   * 绑定到 canvas 元素
   */
  attachToCanvas(canvas: HTMLCanvasElement): void {
    // 先解除之前的绑定
    if (this.canvas) {
      this.detach()
    }

    this.canvas = canvas

    // 绑定鼠标事件
    canvas.addEventListener('mousemove', this.handleMouseMove)
    canvas.addEventListener('mouseenter', this.handleMouseEnter)
    canvas.addEventListener('mouseleave', this.handleMouseLeave)

    // 绑定触摸事件（移动端支持）
    if (this.enableTouch) {
      canvas.addEventListener('touchstart', this.handleTouchStart, { passive: true })
      canvas.addEventListener('touchmove', this.handleTouchMove, { passive: true })
      canvas.addEventListener('touchend', this.handleTouchEnd)
    }
  }

  /**
   * 解除绑定
   */
  detach(): void {
    if (!this.canvas) return

    this.canvas.removeEventListener('mousemove', this.handleMouseMove)
    this.canvas.removeEventListener('mouseenter', this.handleMouseEnter)
    this.canvas.removeEventListener('mouseleave', this.handleMouseLeave)

    if (this.enableTouch) {
      this.canvas.removeEventListener('touchstart', this.handleTouchStart)
      this.canvas.removeEventListener('touchmove', this.handleTouchMove)
      this.canvas.removeEventListener('touchend', this.handleTouchEnd)
    }

    this.canvas = null
    this.state.isInCanvas = false
  }

  /**
   * 获取当前鼠标状态
   */
  getState(): MouseState {
    return { ...this.state }
  }

  /**
   * 检查鼠标是否在指定位置附近
   *
   * @param x 目标 X 坐标
   * @param y 目标 Y 坐标
   * @param radius 检测半径
   */
  isNear(x: number, y: number, radius: number): boolean {
    if (!this.state.isInCanvas) return false

    const dx = this.state.x - x
    const dy = this.state.y - y
    const distance = Math.sqrt(dx * dx + dy * dy)

    return distance <= radius
  }

  /**
   * 计算到指定位置的距离
   */
  getDistanceTo(x: number, y: number): number {
    if (!this.state.isInCanvas) return Infinity

    const dx = this.state.x - x
    const dy = this.state.y - y
    return Math.sqrt(dx * dx + dy * dy)
  }

  /**
   * 重置状态
   */
  reset(): void {
    this.state = {
      x: 0,
      y: 0,
      isInCanvas: false,
      normalizedX: 0,
      normalizedY: 0
    }
  }

  /**
   * 更新位置（内部方法）
   */
  private updatePosition(clientX: number, clientY: number): void {
    if (!this.canvas) return

    const rect = this.canvas.getBoundingClientRect()
    const canvasWidth = this.canvas.width
    const canvasHeight = this.canvas.height

    // 计算相对于 canvas 的坐标
    // 注意：使用 canvas 实际尺寸而非 CSS 显示尺寸
    const scaleX = canvasWidth / rect.width
    const scaleY = canvasHeight / rect.height

    this.state.x = (clientX - rect.left) * scaleX
    this.state.y = (clientY - rect.top) * scaleY

    // 计算归一化坐标
    this.state.normalizedX = Math.max(0, Math.min(1, this.state.x / canvasWidth))
    this.state.normalizedY = Math.max(0, Math.min(1, this.state.y / canvasHeight))
  }

  /**
   * 鼠标移动事件处理
   */
  private handleMouseMove = (e: MouseEvent): void => {
    this.updatePosition(e.clientX, e.clientY)
    this.state.isInCanvas = true
  }

  /**
   * 鼠标进入事件处理
   */
  private handleMouseEnter = (e: MouseEvent): void => {
    this.updatePosition(e.clientX, e.clientY)
    this.state.isInCanvas = true
  }

  /**
   * 鼠标离开事件处理
   */
  private handleMouseLeave = (): void => {
    this.state.isInCanvas = false
  }

  /**
   * 触摸开始事件处理
   */
  private handleTouchStart = (e: TouchEvent): void => {
    if (e.touches.length > 0) {
      const touch = e.touches[0]
      this.updatePosition(touch.clientX, touch.clientY)
      this.state.isInCanvas = true
      this.lastTouchTime = Date.now()
    }
  }

  /**
   * 触摸移动事件处理（带节流）
   */
  private handleTouchMove = (e: TouchEvent): void => {
    const now = Date.now()
    if (now - this.lastTouchTime < this.touchThrottle) return

    if (e.touches.length > 0) {
      const touch = e.touches[0]
      this.updatePosition(touch.clientX, touch.clientY)
      this.state.isInCanvas = true
      this.lastTouchTime = now
    }
  }

  /**
   * 触摸结束事件处理
   */
  private handleTouchEnd = (): void => {
    this.state.isInCanvas = false
  }

  /**
   * 销毁追踪器
   */
  destroy(): void {
    this.detach()
  }
}

/**
 * 创建鼠标追踪器的工厂函数
 */
export function createMouseTracker(
  options?: MouseTrackerOptions
): MouseTracker {
  return new MouseTracker(options)
}